import React, { useEffect, useState } from "react";
import { XCrossIcon } from "../../../../assets";
import { useMessage } from "../../../../context/MessageContext";
import { isEqual } from "lodash";
import { getApi, postApi, putApi } from "../../../../apis/api";
import { GENERAL_ERROR_MESSAGE, SERVICE_CONFIG_MESSAGES } from "../../../../utils/message";
import { Loader } from "../../../../components/loaders/loaders";
import { AddEditZipCodeModalProps } from "../MasterZipCodeTypes";
import { ADD_MASTER_ZIP_CODE, UPDATE_MASTER_ZIP_CODE, GET_DATA_FROM_ZIPCODE } from "../../../../apis/end-points";

const emptyForm = {
  zip: "",
  city: "",
  state: "",
  stateCode: "",
  country: "",
  countryCode: "",
  latitude: "",
  longitude: "",
  active: true,
};

const AddEditZipCodeModal: React.FC<AddEditZipCodeModalProps> = ({
  isEdit = false,
  isOpen,
  onClose,
  initialData = null,
  onSuccess,
}) => {
  const { setMessage } = useMessage();
  const [data, setData] = useState(emptyForm);
  const [initialPayload, setInitialPayload] = useState<any>(null);
  const [isChanged, setIsChanged] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [zipError, setZipError] = useState<string>("");
  const [latError, setLatError] = useState<string>("");
  const [lngError, setLngError] = useState<string>("");

  // Defined before useEffect so it can be safely called inside the effect callback
  const validateLatLng = (field: "latitude" | "longitude", value: string) => {
    const num = Number.parseFloat(value);
    if (value === "" || Number.isNaN(num)) {
      field === "latitude" ? setLatError("") : setLngError("");
      return;
    }
    if (field === "latitude") {
      setLatError(num < -90 || num > 90 ? "Latitude must be between -90 and 90." : "");
    } else {
      setLngError(num < -180 || num > 180 ? "Longitude must be between -180 and 180." : "");
    }
  };

  useEffect(() => {
    if (!isOpen) return;

    if (!isEdit || !initialData) {
      setData(emptyForm);
      setInitialPayload(emptyForm);
      return;
    }

    const payload = {
      zip: initialData.zip || "",
      city: initialData.city || "",
      state: initialData.state || "",
      stateCode: initialData.stateCode || "",
      country: initialData.country || "",
      countryCode: initialData.countryCode || "",
      latitude: initialData.latitude || "",
      longitude: initialData.longitude || "",
      active: initialData.active ?? true,
    };

    setData(payload);
    setInitialPayload(payload);

    // Validate API-prefilled coords so errors surface immediately on open
    validateLatLng("latitude",  payload.latitude);
    validateLatLng("longitude", payload.longitude);
  }, [isEdit, initialData, isOpen]);

  useEffect(() => {
    if (!isEdit || !initialPayload) return;
    setIsChanged(!isEqual(initialPayload, data));
  }, [data, initialPayload, isEdit]);

  const resetForm = () => {
    setData(emptyForm);
    setInitialPayload(null);
    setIsChanged(false);
    setZipError("");
    setLatError("");
    setLngError("");
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const applyZipLookup = async (zip: string) => {
    const apiResponse = await getApi<any>(`${GET_DATA_FROM_ZIPCODE}${zip}`);
    if (!apiResponse?.success || !apiResponse?.data) return;
    const d = apiResponse.data;
    const newLat = d?.latitude ? String(d.latitude) : "";
    const newLng = d?.longitude ? String(d.longitude) : "";
    setData((prev) => ({
      ...prev,
      state:       d?.state       || prev.state,
      stateCode:   d?.stateCode   || prev.stateCode,
      country:     d?.country     || prev.country,
      countryCode: d?.countryCode || prev.countryCode,
      latitude:    newLat         || prev.latitude,
      longitude:   newLng         || prev.longitude,
    }));
    if (newLat) validateLatLng("latitude",  newLat);
    if (newLng) validateLatLng("longitude", newLng);
  };

  const handleZipChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (!/^\d*$/.test(value)) return;
    setData((prev) => ({ ...prev, zip: value }));
    const isInvalidLength = value.length > 0 && (value.length < 5 || value.length > 9);
    setZipError(isInvalidLength ? "Please enter a valid zipcode" : "");
    if (value.length >= 5) await applyZipLookup(value);
  };

  const handleChange = (field: "city" | "latitude" | "longitude") =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      if (field === "city") {
        setData((prev) => ({ ...prev, [field]: value.trimStart() }));
        return;
      }
      // Allow empty, valid decimals, and negative values for lat/lng
      if (value !== "" && !/^-?\d*\.?\d{0,6}$/.test(value)) return;
      setData((prev) => ({ ...prev, [field]: value }));
      validateLatLng(field, value);
    };

  const handleLatLngKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // Block scientific notation (e/E) and explicit plus sign; allow minus for negative coords
    if (e.key === "e" || e.key === "E" || e.key === "+") {
      e.preventDefault();
    }
  };

  const handleLatLngWheel = (e: React.WheelEvent<HTMLInputElement>) => {
    // Prevent accidental value change when scrolling over the field
    (e.target as HTMLInputElement).blur();
  };

  const STEP = 0.000001;
  const LAT_BOUNDS  = { min: -90,  max: 90  };
  const LNG_BOUNDS  = { min: -180, max: 180 };

  const handleStep = (field: "latitude" | "longitude", direction: 1 | -1) => {
    const bounds  = field === "latitude" ? LAT_BOUNDS : LNG_BOUNDS;
    const current = Number.parseFloat(data[field]) || 0;
    const next    = Number.parseFloat((current + direction * STEP).toFixed(6));
    if (next < bounds.min || next > bounds.max) return;
    setData((prev) => ({ ...prev, [field]: String(next) }));
    validateLatLng(field, String(next)); // clears stale error from a prior manual entry
  };

  const isSaveDisabled = () => {
    if (!data.zip.trim() || data.zip.trim().length < 5 || data.zip.trim().length > 9) return true;
    if (!data.city.trim() || !data.state.trim() || !data.country.trim()) return true;
    if (latError || lngError) return true;
    if (isEdit && !isChanged) return true;
    return false;
  };

  const handleSave = async () => {
    const payload = {
      zipCode: data.zip.trim(),
      city: data.city.trim(),
      state: data.state.trim(),
      stateCode: data.stateCode.trim(),
      country: data.country.trim(),
      countryCode: data.countryCode.trim(),
      lat: data.latitude.trim(),
      lng: data.longitude.trim(),
      isActive: data.active,
    };

    try {
      setLoading(true);
      const apiResponse = isEdit && initialData?.id
        ? await putApi(`${UPDATE_MASTER_ZIP_CODE}${initialData.id}`, payload)
        : await postApi(ADD_MASTER_ZIP_CODE, payload);

      if (apiResponse?.success) {
        setMessage(
          isEdit ? SERVICE_CONFIG_MESSAGES.ZIP_CODE_UPDATE_SUCCESS : SERVICE_CONFIG_MESSAGES.ZIP_CODE_ADD_SUCCESS,
          "success"
        );
        onSuccess?.();
        handleClose();
      } else {
        setMessage(apiResponse?.error?.message ?? GENERAL_ERROR_MESSAGE, "error");
      }
    } catch {
      setMessage(GENERAL_ERROR_MESSAGE, "error");
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <>
    <div className="modal modal--hazmatDetail modal--open">
      <div className="modal__dialog">
        <div className="modal__content modal__content--lg">
          <div className="modal__header">
            <h4 className="modal__header--title">
              {isEdit ? "Edit Zip Code" : "Add New Zip Code"}
            </h4>
            <button className="btn btn__transparent btnClose" onClick={handleClose} type="button">
              <XCrossIcon />
            </button>
          </div>

          <div className="modal__body modal-body-left">
            <div className="formRow formRow__2">
              <div className="formCol">
                <div className="form__Field">
                  <label htmlFor="zip" className="form__label">
                    Zipcode <span className="mandatory-mark">*</span>
                  </label>
                  <input
                    id="zip"
                    type="text"
                    placeholder="Enter Zipcode"
                    className="form__input form__input--sm"
                    value={data.zip}
                    onChange={handleZipChange}
                  />
                  {zipError && (
                    <span className="form__error">{zipError}</span>
                  )}
                </div>
              </div>

              <div className="formCol">
                <div className="form__Field">
                  <label htmlFor="city" className="form__label">
                    City <span className="mandatory-mark">*</span>
                  </label>
                  <input
                    id="city"
                    type="text"
                    placeholder="Enter City"
                    className="form__input form__input--sm"
                    value={data.city}
                    onChange={handleChange("city")}
                  />
                </div>
              </div>

              <div className="formCol">
                <div className="form__Field">
                  <label htmlFor="state" className="form__label">
                    State <span className="mandatory-mark">*</span>
                  </label>
                  <input
                    id="state"
                    type="text"
                    placeholder="Enter State"
                    className="form__input form__input--sm form__input--disabled"
                    disabled
                    value={data.state}
                    onChange={() => {}}
                  />
                </div>
              </div>

              <div className="formCol">
                <div className="form__Field">
                  <label htmlFor="country" className="form__label">
                    Country <span className="mandatory-mark">*</span>
                  </label>
                  <input
                    id="country"
                    type="text"
                    placeholder="Enter Country"
                    className="form__input form__input--sm form__input--disabled"
                    disabled
                    value={data.country}
                    onChange={() => {}}
                  />
                </div>
              </div>

              <div className="formCol">
                <div className="form__Field">
                  <label htmlFor="latitude" className="form__label">
                    Latitude
                  </label>
                  <div style={{ display: "flex", alignItems: "stretch" }}>
                    <input
                      id="latitude"
                      type="number"
                      placeholder="Enter Latitude"
                      className="form__input form__input--sm"
                      style={{ borderRadius: "0.375rem 0 0 0.375rem", borderRight: "none", flex: 1 }}
                      value={data.latitude}
                      step="0.000001"
                      min="-90"
                      max="90"
                      onChange={handleChange("latitude")}
                      onKeyDown={handleLatLngKeyDown}
                      onWheel={handleLatLngWheel}
                    />

                    <div style={{
                      display: "flex", flexDirection: "column",
                      border: "1px solid rgba(102,102,102,0.35)",
                      borderLeft: "none",
                      borderRadius: "0 0.375rem 0.375rem 0",
                      overflow: "hidden",
                    }}>
                      <button
                        type="button"
                        title="Increment latitude"
                        onClick={() => handleStep("latitude", 1)}
                        style={{
                          flex: 1, background: "#fff", border: "none",
                          borderBottom: "1px solid rgba(102,102,102,0.35)",
                          cursor: "pointer", padding: "0 8px",
                          fontSize: "10px", lineHeight: 1,
                          color: "#150823", display: "flex",
                          alignItems: "center", justifyContent: "center",
                        }}
                      >▲</button>
                      <button
                        type="button"
                        title="Decrement latitude"
                        onClick={() => handleStep("latitude", -1)}
                        style={{
                          flex: 1, background: "#fff", border: "none",
                          cursor: "pointer", padding: "0 8px",
                          fontSize: "10px", lineHeight: 1,
                          color: "#150823", display: "flex",
                          alignItems: "center", justifyContent: "center",
                        }}
                      >▼</button>
                    </div>
                  </div>
                  {latError && (
                    <span className="form__error">{latError}</span>
                  )}
                </div>
              </div>

              <div className="formCol">
                <div className="form__Field">
                  <label htmlFor="longitude" className="form__label">
                    Longitude
                  </label>
                  <div style={{ display: "flex", alignItems: "stretch" }}>
                    <input
                      id="longitude"
                      type="number"
                      placeholder="Enter Longitude"
                      className="form__input form__input--sm"
                      style={{ borderRadius: "0.375rem 0 0 0.375rem", borderRight: "none", flex: 1 }}
                      value={data.longitude}
                      step="0.000001"
                      min="-180"
                      max="180"
                      onChange={handleChange("longitude")}
                      onKeyDown={handleLatLngKeyDown}
                      onWheel={handleLatLngWheel}
                    />
                    <div style={{
                      display: "flex", flexDirection: "column",
                      border: "1px solid rgba(102,102,102,0.35)",
                      borderLeft: "none",
                      borderRadius: "0 0.375rem 0.375rem 0",
                      overflow: "hidden",
                    }}>
                      <button
                        type="button"
                        title="Increment longitude"
                        onClick={() => handleStep("longitude", 1)}
                        style={{
                          flex: 1, background: "#fff", border: "none",
                          borderBottom: "1px solid rgba(102,102,102,0.35)",
                          cursor: "pointer", padding: "0 8px",
                          fontSize: "10px", lineHeight: 1,
                          color: "#150823", display: "flex",
                          alignItems: "center", justifyContent: "center",
                        }}
                      >▲</button>
                      <button
                        type="button"
                        title="Decrement longitude"
                        onClick={() => handleStep("longitude", -1)}
                        style={{
                          flex: 1, background: "#fff", border: "none",
                          cursor: "pointer", padding: "0 8px",
                          fontSize: "10px", lineHeight: 1,
                          color: "#150823", display: "flex",
                          alignItems: "center", justifyContent: "center",
                        }}
                      >▼</button>
                    </div>
                  </div>
                  {lngError && (
                    <span className="form__error">{lngError}</span>
                  )}
                </div>
              </div>
            </div>

            <div className="formRow">
              <div className="formCol">
                <div className="form__Field">
                  <div className="form__switchBtn">
                    <div className="form__switchBtn__option">
                      <div className="form__label">Active</div>
                      <div className="switchBtn">
                        <input
                          type="checkbox"
                          className="toggle"
                          name="isActive"
                          id="isActive"
                          checked={data.active}
                          onChange={() => setData((prev) => ({ ...prev, active: !prev.active }))}
                        />
                        <label htmlFor="isActive">
                          <span className="on">Yes</span>
                          <span className="off">No</span>
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="modal__footer">
            <div className="btn__group">
              <button className="btn btn__white" onClick={handleClose} type="button">
                Cancel
              </button>
              <button
                className="btn btn__primary"
                onClick={handleSave}
                type="button"
                disabled={isSaveDisabled()}
              >
                {isEdit ? "Update" : "Save"}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="modal__backdrop"></div>
    </div>
    {loading && <Loader />}
    </>
  );
};

export default AddEditZipCodeModal;
